import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getProducts, getLiveData, verifyLive, addProduct, updateProduct, deleteProduct, getToken } from '../api/client';

const fI = (n, sym) => (n || n === 0) ? (sym || '₹') + Math.round(n).toLocaleString('en-IN') : '—';
const amzUrl = (asin) => `https://www.amazon.in/dp/${asin}`;

function timeAgo(iso) {
  if (!iso) return 'Never';
  const d = (Date.now() - new Date(iso + 'Z').getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return Math.floor(d / 60) + 'm ago';
  if (d < 86400) return Math.floor(d / 3600) + 'h ago';
  return Math.floor(d / 86400) + 'd ago';
}

function fmtDuration(iso) {
  if (!iso) return null;
  const d = (Date.now() - new Date(iso + 'Z').getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return Math.floor(d / 60) + 'm';
  if (d < 86400) return Math.floor(d / 3600) + 'h';
  return Math.floor(d / 86400) + 'd';
}

// column config: key = accessor into the merged row object, filterable = show Excel-style filter (search + checkboxes + sort)
const COLS = [
  { key: '_remove', l: '', w: 40, sortable: false, filterable: false },
  { key: '_pin', l: '', w: 26, sortable: false, filterable: false },
  { key: '_chart', l: '', w: 30, sortable: false, filterable: false },
  { key: '_tracking', l: 'Track', w: 46, sortable: false, filterable: false },
  { key: 'name', l: 'Product', w: 170, sortable: true, filterable: true },
  { key: 'asin', l: 'ASIN', w: 90, sortable: true, filterable: true },
  { key: 'price', l: 'Live Price', w: 100, sortable: true, filterable: false, type: 'num' },
  { key: 'last_price_change_at', l: 'Last Changed', w: 100, sortable: true, filterable: false },
  { key: 'price_stable_since', l: 'Stable For', w: 90, sortable: true, filterable: false },
  { key: 'list_price', l: 'List Price', w: 100, sortable: true, filterable: false, type: 'num' },
  { key: 'availability_text', l: 'Stock', w: 90, sortable: true, filterable: true },
  { key: 'ck_stock', l: 'Clicktech Stock', w: 100, sortable: true, filterable: false, type: 'num' },
  { key: 'ck_delta', l: 'Stock Change', w: 90, sortable: true, filterable: false, type: 'num' },
  { key: 'ck_stock_at', l: 'Stock Checked', w: 100, sortable: true, filterable: false },
  { key: 'has_buy_box', l: 'Buy Box', w: 70, sortable: true, filterable: true, type: 'bool' },
  { key: 'is_prime', l: 'Prime', w: 60, sortable: true, filterable: true, type: 'bool' },
  { key: 'amazon_choice', l: "Amazon's Choice", w: 100, sortable: true, filterable: true, type: 'bool' },
  { key: 'rating', l: 'Rating', w: 70, sortable: true, filterable: false, type: 'num' },
  { key: 'total_ratings', l: 'Reviews', w: 70, sortable: true, filterable: false, type: 'num' },
  { key: 'brand', l: 'Brand', w: 110, sortable: true, filterable: true },
  { key: 'sold_by', l: 'Sold By', w: 160, sortable: true, filterable: true },
  { key: 'other_sellers_count', l: 'Other Sellers', w: 90, sortable: true, filterable: false, type: 'num' },
  { key: 'best_seller_rank', l: 'Best Seller Rank', w: 190, sortable: true, filterable: false },
  { key: 'model_number', l: 'Model Number', w: 130, sortable: true, filterable: true },
  { key: 'country_of_origin', l: 'Country of Origin', w: 120, sortable: true, filterable: true },
  { key: 'warranty', l: 'Warranty', w: 220, sortable: true, filterable: true },
  { key: 'item_weight', l: 'Weight', w: 90, sortable: true, filterable: false },
  { key: 'screen_size', l: 'Screen Size', w: 100, sortable: true, filterable: true },
  { key: 'estimated_delivery', l: 'Est. Delivery', w: 140, sortable: true, filterable: false },
  { key: 'max_quantity', l: 'Max Qty', w: 70, sortable: true, filterable: false, type: 'num' },
  { key: 'variants_count', l: 'Variants', w: 70, sortable: true, filterable: false, type: 'num' },
  { key: 'checked_at', l: 'Last Verified', w: 90, sortable: true, filterable: false },
];

function FilterPopover({ col, values, filterState, onSearchChange, onCheckChange, onSort, onClose }) {
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  const search = filterState?.search || '';
  const checked = filterState?.checked || null;
  const shown = search ? values.filter(v => String(v).toLowerCase().includes(search.toLowerCase())) : values;
  const allChecked = !checked || checked.size === values.length;
  const isNum = col.type === 'num';
  const sortBtn = { display: 'block', width: '100%', textAlign: 'left', padding: '5px 6px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#1a1917', borderRadius: 4 };
  return (
    <div ref={ref} style={{ position: 'absolute', top: 26, left: 0, zIndex: 50, background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e0d8)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.15)', padding: 10, width: 210, fontFamily: 'DM Sans,sans-serif', fontWeight: 400, textTransform: 'none' }}>
      <div style={{ borderBottom: '1px solid var(--border,#e2e0d8)', paddingBottom: 6, marginBottom: 6 }}>
        <button onClick={() => onSort('asc')} style={sortBtn} onMouseEnter={e=>e.currentTarget.style.background='#f0efe9'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>{isNum ? '↑ Smallest to Largest' : '↑ A to Z'}</button>
        <button onClick={() => onSort('desc')} style={sortBtn} onMouseEnter={e=>e.currentTarget.style.background='#f0efe9'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>{isNum ? '↓ Largest to Smallest' : '↓ Z to A'}</button>
      </div>
      <input value={search} onChange={e => onSearchChange(e.target.value)} placeholder="Search (word or letter)..."
        style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '1px solid #e2e0d8', borderRadius: 5, fontSize: 12, marginBottom: 8, outline: 'none' }} />
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        <div onClick={() => onCheckChange(allChecked ? new Set() : new Set(values))} style={{ fontSize: 11, fontWeight: 600, cursor: 'pointer', marginBottom: 6, color: '#1a1917' }}>
          {allChecked ? 'Clear all' : 'Select all'}
        </div>
        {shown.map(v => (
          <label key={String(v)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0', cursor: 'pointer', color: '#1a1917' }}>
            <input type="checkbox" checked={!checked || checked.has(v)} onChange={() => {
              const next = new Set(checked || values);
              if (next.has(v)) next.delete(v); else next.add(v);
              onCheckChange(next.size === values.length ? null : next);
            }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(v) || '(blank)'}</span>
          </label>
        ))}
        {shown.length === 0 && <div style={{ fontSize: 11, color: '#9b9890', padding: '4px 0' }}>No matches</div>}
      </div>
    </div>
  );
}

const DOTS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6', '#84cc16', '#f97316', '#6366f1', '#06b6d4', '#78716c'];

export default function LiveVerify({ dark }) {
  const [products, setProducts] = useState([]);
  const [liveData, setLiveData] = useState({});
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState({});
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newAsin, setNewAsin] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [chartProduct, setChartProduct] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({}); // { [key]: { search, checked: Set|null } }
  const [openFilter, setOpenFilter] = useState(null);

  const load = useCallback(async () => {
    try {
      const [pRes, lRes] = await Promise.all([getProducts(), getLiveData()]);
      setProducts(pRes.data);
      const map = {};
      lRes.data.forEach(row => { map[row.product_id] = row; });
      setLiveData(map);
    } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const doVerify = async (id) => {
    setVerifying(v => ({ ...v, [id]: true }));
    try {
      const r = await verifyLive(id);
      setLiveData(d => ({ ...d, [id]: r.data }));
    } catch (e) {
      alert(e.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifying(v => ({ ...v, [id]: false }));
    }
  };

  const doVerifyAll = async () => {
    setVerifyingAll(true);
    for (const p of visible) { await doVerify(p.id); }
    setVerifyingAll(false);
  };

  const handleAdd = async () => {
    const asin = newAsin.trim().toUpperCase();
    if (!/^[A-Z0-9]{10}$/.test(asin)) { alert('Enter a valid 10-character Amazon ASIN'); return; }
    setAdding(true);
    try {
      const r = await addProduct(asin, amzUrl(asin));
      setNewAsin(''); setShowAdd(false); await load();
      if (r.data?.id) doVerify(r.data.id);
    } catch (e) { alert(e.response?.data?.detail || 'Error adding product'); }
    finally { setAdding(false); }
  };

  const togglePin = async (p) => {
    await updateProduct(p.id, { pinned: !p.pinned, pin_order: p.pinned ? 0 : Date.now() });
    await load();
  };

  const toggleTracking = async (p) => {
    setProducts(ps => ps.map(x => x.id === p.id ? { ...x, scrapedo_tracking: !p.scrapedo_tracking } : x));
    try { await updateProduct(p.id, { scrapedo_tracking: !p.scrapedo_tracking }); }
    catch (e) { setProducts(ps => ps.map(x => x.id === p.id ? { ...x, scrapedo_tracking: p.scrapedo_tracking } : x)); alert(e.response?.data?.detail || 'Failed to update tracking'); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try { await deleteProduct(confirmDel.id); setConfirmDel(null); await load(); }
    catch (e) { alert(e.response?.data?.detail || 'Remove failed'); }
    finally { setDeleting(false); }
  };

  const openChart = (id) => {
    setChartProduct(id); setChartData([]); setChartLoading(true);
    const token = getToken();
    fetch('/api/products/' + id + '/scrapedo-history', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json()).then(d => { setChartData(Array.isArray(d) ? d : []); setChartLoading(false); })
      .catch(() => setChartLoading(false));
  };

  const merged = useMemo(() => products.map(p => {
    const live = liveData[p.id] || {};
    const ckDelta = (live.ck_stock != null && live.ck_stock_prev != null) ? live.ck_stock - live.ck_stock_prev : null;
    return { ...p, ...live, id: p.id, ck_delta: ckDelta, price_stable_since: live.last_price_change_at };
  }), [products, liveData]);

  const q = search.toLowerCase();
  const visible = useMemo(() => {
    let rows = q ? merged.filter(r => (r.group_label && r.group_label===q) || r.name.toLowerCase().includes(q) || r.asin.toLowerCase().includes(q)) : merged;
    for (const [key, state] of Object.entries(filters)) {
      if (!state) continue;
      const col = COLS.find(c => c.key === key);
      rows = rows.filter(r => {
        let v = r[key];
        if (col?.type === 'bool') v = v ? 'Yes' : 'No';
        else v = v ?? '—';
        const sv = String(v).toLowerCase();
        if (state.search && !sv.includes(state.search.toLowerCase())) return false;
        if (state.checked && !state.checked.has(v)) return false;
        return true;
      });
    }
    if (sortKey) {
      const col = COLS.find(c => c.key === sortKey);
      rows = [...rows].sort((a, b) => {
        let av = a[sortKey], bv = b[sortKey];
        if (col?.type === 'num') { av = av ?? -Infinity; bv = bv ?? -Infinity; }
        else { av = (av ?? '').toString().toLowerCase(); bv = (bv ?? '').toString().toLowerCase(); }
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      rows = [...rows].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || (b.pin_order || 0) - (a.pin_order || 0));
    }
    return rows;
  }, [merged, q, filters, sortKey, sortDir]);

  const uniqueValuesFor = (key) => {
    const col = COLS.find(c => c.key === key);
    const set = new Set();
    merged.forEach(r => {
      let v = r[key];
      if (col?.type === 'bool') v = v ? 'Yes' : 'No';
      else v = v ?? '—';
      set.add(v);
    });
    return Array.from(set).sort();
  };

  const handleSortClick = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else { setSortKey(null); setSortDir('asc'); }
  };

  const setFilterSearch = (key, val) => setFilters(f => ({ ...f, [key]: { ...(f[key] || {}), search: val } }));
  const setFilterChecked = (key, val) => setFilters(f => ({ ...f, [key]: { ...(f[key] || {}), checked: val } }));

  const td = { padding: '5px 10px', fontSize: 11, fontFamily: 'DM Mono,monospace', borderBottom: '1px solid rgba(0,0,0,.06)', borderRight: '1px solid rgba(0,0,0,.04)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const thBase = { padding: '6px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: dark ? '#9b9890' : '#6b6960', textAlign: 'left', borderBottom: '1px solid var(--border,#e2e0d8)', borderRight: '1px solid rgba(0,0,0,.04)', fontFamily: 'DM Sans,sans-serif', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: dark ? '#1a1917' : '#f8f7f4', zIndex: 2 };

  const Badge = ({ ok, yesLabel = 'Yes', noLabel = 'No' }) => (
    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, fontFamily: 'DM Sans,sans-serif', background: ok ? 'rgba(16,185,129,.15)' : 'rgba(156,163,175,.15)', color: ok ? '#059669' : '#6b6960' }}>
      {ok ? yesLabel : noLabel}
    </span>
  );

  const btnDark = { padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif', border: 'none', background: '#1a1917', color: '#fff' };
  const btnOut = { padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif', border: '1px solid var(--border,#e2e0d8)', background: 'none', color: 'var(--text,#1a1917)' };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9b9890' }}>Loading...</div>;

  const renderCell = (col, r) => {
    switch (col.key) {
      case '_remove': return <button onClick={() => setConfirmDel(r)} title="Remove product" style={{ width: 20, height: 20, borderRadius: 5, border: '1px solid #fecaca', background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: 11, lineHeight: 1 }}>✕</button>;
      case '_pin': return <button onClick={() => togglePin(r)} style={{ width: 22, height: 22, borderRadius: 5, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: r.pinned ? '#f59e0b' : 'rgba(128,128,128,.4)' }}>{r.pinned ? '📌' : '📍'}</button>;
      case '_chart': return <button onClick={() => openChart(r.id)} style={{ width: 22, height: 22, borderRadius: 5, border: '1px solid #e2e0d8', background: 'transparent', cursor: 'pointer', fontSize: 11 }} title="Price history (our pulls)">📈</button>;
      case '_tracking': {
        const on = r.scrapedo_tracking !== false;
        return (
          <div onClick={() => toggleTracking(r)} title={on ? 'Auto-tracking on — included in the 5-min sweep' : 'Auto-tracking off — excluded from the 5-min sweep'}
            style={{ width: 30, height: 17, background: on ? '#22c55e' : '#cccac0', borderRadius: 9, position: 'relative', cursor: 'pointer', margin: '0 auto', flexShrink: 0 }}>
            <div style={{ position: 'absolute', top: 2, left: on ? 15 : 2, width: 13, height: 13, background: 'white', borderRadius: '50%', transition: 'left .15s' }}></div>
          </div>
        );
      }
      case 'name': return <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>{r.model_group ? <span title={`Same model (${r.group_label}) — ${r.group_size} listings`} onClick={() => setSearch(r.group_label)} style={{ flex: 'none', width: 11, height: 11, borderRadius: '50%', background: DOTS[(r.model_group - 1) % DOTS.length], cursor: 'pointer', display: 'inline-block' }} /> : null}<a href={amzUrl(r.asin)} target="_blank" rel="noopener noreferrer" title={r.name} style={{ color: 'var(--text,#1a1917)', textDecoration: 'none' }} onMouseEnter={e=>e.currentTarget.style.textDecoration='underline'} onMouseLeave={e=>e.currentTarget.style.textDecoration='none'}>{r.name}</a></span>;
      case 'asin': return r.asin;
      case 'price': return <span style={{ fontWeight: 600 }}>{fI(r.price, r.currency_symbol)}</span>;
      case 'last_price_change_at': return r.last_price_change_at ? <span title={new Date(r.last_price_change_at + 'Z').toLocaleString('en-IN')} style={{ color: '#6b6960' }}>{timeAgo(r.last_price_change_at)}</span> : '—';
      case 'price_stable_since': return r.price_stable_since ? <span title={new Date(r.price_stable_since + 'Z').toLocaleString('en-IN')} style={{ color: '#6b6960' }}>{fmtDuration(r.price_stable_since)}</span> : '—';
      case 'list_price': return fI(r.list_price, r.currency_symbol);
      case 'availability_text': return r.availability_text || '—';
      case 'ck_stock': return r.ck_stock != null ? <span style={{ fontWeight: 600 }}>{r.ck_stock.toLocaleString('en-IN')}</span> : '—';
      case 'ck_delta': return r.ck_delta ? <span style={{ fontWeight: 600, color: r.ck_delta < 0 ? '#dc2626' : '#059669' }}>{r.ck_delta > 0 ? '+' : ''}{r.ck_delta}</span> : (r.ck_delta === 0 ? '0' : '—');
      case 'ck_stock_at': return r.ck_stock_at ? <span style={{ color: '#9b9890' }}>{timeAgo(r.ck_stock_at)}</span> : '—';
      case 'has_buy_box': return r.checked_at ? <Badge ok={r.has_buy_box} /> : '—';
      case 'is_prime': return r.checked_at ? <Badge ok={r.is_prime} /> : '—';
      case 'amazon_choice': return r.checked_at ? <Badge ok={r.amazon_choice} /> : '—';
      case 'rating': return r.rating != null ? `${r.rating} ★` : '—';
      case 'total_ratings': return r.total_ratings != null ? r.total_ratings.toLocaleString('en-IN') : '—';
      case 'brand': return r.brand || '—';
      case 'sold_by': return r.sold_by || '—';
      case 'other_sellers_count': return r.other_sellers_count != null ? r.other_sellers_count : '—';
      case 'best_seller_rank': return r.best_seller_rank ? <span>#{r.best_seller_rank} in {r.best_seller_category}{r.broad_rank && <span style={{ color: '#9b9890', fontSize: 10 }}> (#{r.broad_rank} in {r.broad_category})</span>}</span> : '—';
      case 'model_number': return r.model_number || '—';
      case 'country_of_origin': return r.country_of_origin || '—';
      case 'warranty': return r.warranty || '—';
      case 'item_weight': return r.item_weight || '—';
      case 'screen_size': return r.screen_size || '—';
      case 'estimated_delivery': return r.estimated_delivery || '—';
      case 'max_quantity': return r.max_quantity != null ? r.max_quantity : '—';
      case 'variants_count': return r.variants_count != null ? r.variants_count : '—';
      case 'checked_at': return <span style={{ color: '#9b9890' }}>{r.checked_at ? timeAgo(r.checked_at) : 'Never'}</span>;
      default: return '—';
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text,#1a1917)', marginBottom: 4, fontFamily: 'DM Sans,sans-serif' }}>Main Scraper</div>
          <div style={{ fontSize: 13, color: '#9b9890', fontFamily: 'DM Sans,sans-serif' }}>Live Amazon.in data via scrape.do — auto-verified in the background. Click a header to sort, use the ▾ to search/filter/sort.</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ASIN or product name..."
            style={{ padding: '9px 12px', border: '1px solid #e2e0d8', borderRadius: 6, fontSize: 13, fontFamily: 'DM Sans,sans-serif', color: 'var(--text,#1a1917)', background: 'var(--surface,#fff)', width: 220 }} />
          <button onClick={() => setShowAdd(s => !s)} style={btnOut}>+ Add product</button>
          {Object.values(filters).some(f => f && (f.search || f.checked)) && <button onClick={() => setFilters({})} style={btnOut}>Clear filters</button>}
          <button onClick={doVerifyAll} disabled={verifyingAll} style={{ ...btnDark, opacity: verifyingAll ? .6 : 1 }}>
            {verifyingAll ? `Verifying... (${visible.length})` : `Verify All (${visible.length})`}
          </button>
        </div>
      </div>

      {showAdd && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
          <input value={newAsin} onChange={e => setNewAsin(e.target.value)} placeholder="ASIN (e.g. B0D2Y1BLDT)"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            style={{ padding: '9px 12px', border: '1px solid #e2e0d8', borderRadius: 6, fontSize: 13, fontFamily: 'DM Mono,monospace', color: 'var(--text,#1a1917)', background: 'var(--surface,#fff)', width: 220 }} />
          <button onClick={handleAdd} disabled={adding} style={{ ...btnDark, opacity: adding ? .6 : 1 }}>{adding ? 'Adding...' : 'Add & verify'}</button>
          <button onClick={() => { setShowAdd(false); setNewAsin(''); }} style={btnOut}>Cancel</button>
        </div>
      )}

      <div style={{ border: '1px solid var(--border,#e2e0d8)', borderRadius: 10, overflow: 'auto', maxHeight: '72vh' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 2800, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              {COLS.map(col => (
                <th key={col.key} style={{ ...thBase, width: col.w, minWidth: col.w, cursor: col.sortable ? 'pointer' : 'default' }}
                  onClick={col.sortable ? () => handleSortClick(col.key) : undefined}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {col.l}
                    {col.sortable && sortKey === col.key && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                    {col.filterable && (
                      <span onClick={e => { e.stopPropagation(); setOpenFilter(openFilter === col.key ? null : col.key); }}
                        style={{ marginLeft: 'auto', color: (filters[col.key]?.search || filters[col.key]?.checked) ? '#1a1917' : '#c4c2ba', fontSize: 10 }}>▾</span>
                    )}
                  </span>
                  {col.filterable && openFilter === col.key && (
                    <FilterPopover col={col} values={uniqueValuesFor(col.key)} filterState={filters[col.key]}
                      onSearchChange={(v) => setFilterSearch(col.key, v)}
                      onCheckChange={(v) => setFilterChecked(col.key, v)}
                      onSort={(dir) => { setSortKey(col.key); setSortDir(dir); setOpenFilter(null); }}
                      onClose={() => setOpenFilter(null)} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(r => {
              const isVerifying = verifying[r.id];
              return (
                <tr key={r.id} style={{ background: 'transparent' }}>
                  {COLS.map(col => (
                    <td key={col.key} style={{
                      ...td,
                      borderRight: col.key === 'checked_at' ? 'none' : td.borderRight,
                      textAlign: ['_remove', '_pin', '_chart', '_tracking'].includes(col.key) ? 'center' : 'left',
                      ...(col.key === 'warranty' ? { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', wordBreak: 'break-word', lineHeight: 1.4, verticalAlign: 'top' } : {}),
                    }}>
                      {renderCell(col, r)}
                    </td>
                  ))}
                  <td style={{ ...td, borderRight: 'none' }}>
                    <button onClick={() => doVerify(r.id)} disabled={isVerifying}
                      style={{ padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans,sans-serif', border: '1px solid var(--border,#e2e0d8)', background: 'var(--surface,#fff)', color: 'var(--text,#1a1917)', opacity: isVerifying ? .6 : 1 }}>
                      {isVerifying ? '...' : 'Verify'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {confirmDel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'pointer' }} onClick={() => !deleting && setConfirmDel(null)}>
          <div style={{ background: 'var(--surface,#fff)', borderRadius: 12, padding: 24, maxWidth: 400, width: '90%', cursor: 'default' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, fontFamily: 'DM Sans,sans-serif', color: 'var(--text,#1a1917)' }}>Remove this product?</div>
            <div style={{ fontSize: 13, color: '#6b6960', marginBottom: 6, fontFamily: 'DM Sans,sans-serif' }}>{confirmDel.name}</div>
            <div style={{ fontSize: 12, color: '#9b9890', fontFamily: 'DM Mono,monospace', marginBottom: 20 }}>{confirmDel.asin}</div>
            <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 20, fontFamily: 'DM Sans,sans-serif' }}>This removes it from tracking and this dashboard. Cannot be undone from here.</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={doDelete} disabled={deleting} style={{ ...btnOut, borderColor: '#dc2626', color: '#dc2626' }}>{deleting ? 'Removing...' : 'Remove'}</button>
              <button onClick={() => setConfirmDel(null)} disabled={deleting} style={btnOut}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {chartProduct && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'pointer' }} onClick={() => setChartProduct(null)}>
          <div style={{ background: 'var(--surface,#fff)', borderRadius: 12, padding: 24, width: 640, maxWidth: '92%', cursor: 'default' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'DM Sans,sans-serif', color: 'var(--text,#1a1917)' }}>Price history — our scrape.do pulls</div>
              <button onClick={() => setChartProduct(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#9b9890' }}>✕</button>
            </div>
            {chartLoading ? <div style={{ padding: 40, textAlign: 'center', color: '#9b9890' }}>Loading...</div> : (
              chartData.length < 2 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9b9890', fontSize: 13, fontFamily: 'DM Sans,sans-serif' }}>
                  {chartData.length === 0 ? 'No pulls yet for this product.' : 'Only one pull so far — check back later to see a trend.'}
                </div>
              ) : (() => {
                const W = 580, H = 220, pad = 30;
                const prices = chartData.map(d => d.price);
                const mn = Math.min(...prices), mx = Math.max(...prices);
                const range = mx - mn || 1;
                const pts = chartData.map((d, i) => {
                  const x = pad + (i / (chartData.length - 1)) * (W - pad * 2);
                  const y = H - pad - ((d.price - mn) / range) * (H - pad * 2);
                  return { x, y, ...d };
                });
                const path = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p.x + ',' + p.y).join(' ');
                return (
                  <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
                    <path d={path} fill="none" stroke="#1a1917" strokeWidth="2" />
                    {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="3" fill="#1a1917" />)}
                    <text x={pad} y={16} fontSize="11" fontFamily="DM Mono,monospace" fill="#6b6960">₹{Math.round(mx).toLocaleString('en-IN')}</text>
                    <text x={pad} y={H - 10} fontSize="11" fontFamily="DM Mono,monospace" fill="#6b6960">₹{Math.round(mn).toLocaleString('en-IN')}</text>
                  </svg>
                );
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
}
